import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";

const API = "/api";

function toDateInputValue(value) {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const match = /^(\d{2})[-/](\d{2})[-/](\d{4})$/.exec(value);
  if (match) return `${match[3]}-${match[2]}-${match[1]}`;

  return "";
}

function DetailTile({ label, value }) {
  return (
    <div className="border rounded-3 p-3 bg-light h-100">
      <div className="text-muted small fw-bold">{label}</div>
      <div className="fw-bold text-dark mt-1">{value || "-"}</div>
    </div>
  );
}

function StudentVerification() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    registration_no: searchParams.get("reg") || searchParams.get("registration_no") || "",
    dob: toDateInputValue(searchParams.get("dob") || ""),
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const isAutoLink = useMemo(() => Boolean(searchParams.get("reg") || searchParams.get("registration_no")), [searchParams]);

  const verifyStudent = async (nextForm = form) => {
    if (!nextForm.registration_no || !nextForm.dob) {
      setMessage("Registration number and date of birth are required.");
      return;
    }

    try {
      setLoading(true);
      setMessage("");
      const res = await axios.get(`${API}/student-verification`, {
        params: {
          registration_no: nextForm.registration_no,
          dob: nextForm.dob,
        },
      });
      setResult(res.data);
    } catch (error) {
      setResult(null);
      setMessage(error.response?.data?.message || "Student verification failed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (form.registration_no && form.dob) {
      verifyStudent(form);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const student = result?.student || {};

  return (
    <div style={{ background: "linear-gradient(135deg, #eef6ff 0%, #ffffff 45%, #fff7ed 100%)", minHeight: "calc(100vh - 140px)", padding: "42px 16px" }}>
      <div className="container" style={{ maxWidth: "980px" }}>
        <div className="bg-white border shadow-sm rounded-3 p-4 mb-3">
          <p className="text-danger fw-bold small text-uppercase mb-1">Official Verification</p>
          <h1 className="h2 mb-2">Student Verification</h1>
          <p className="text-muted mb-0">Registration number and date of birth diye approved student record verify korun.</p>
        </div>

        <div className="bg-white border shadow-sm rounded-3 p-4 mb-3">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              verifyStudent();
            }}
            className="row g-3 align-items-end"
          >
            <div className="col-md-5">
              <label className="form-label fw-bold">Registration Number</label>
              <input
                className="form-control"
                value={form.registration_no}
                onChange={(event) => setForm((prev) => ({ ...prev, registration_no: event.target.value }))}
                placeholder="Enter registration number"
                required
              />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-bold">Date of Birth</label>
              <input
                className="form-control"
                type="date"
                value={form.dob}
                onChange={(event) => setForm((prev) => ({ ...prev, dob: event.target.value }))}
                required
              />
            </div>
            <div className="col-md-3">
              <button className="btn btn-success w-100" disabled={loading}>
                {loading ? "Checking..." : "Verify Student"}
              </button>
            </div>
          </form>
          {isAutoLink ? <div className="text-success fw-bold small mt-2">QR details loaded automatically.</div> : null}
          {message ? <div className="text-danger fw-bold mt-2">{message}</div> : null}
        </div>

        {result ? (
          <div className="bg-white border shadow-sm rounded-3 overflow-hidden">
            <div className="p-4 text-white" style={{ background: "linear-gradient(135deg, #4e1b24 0%, #7c2d3a 48%, #c2410c 100%)" }}>
              <p className="small fw-bold text-uppercase mb-1" style={{ opacity: 0.8 }}>Verified Student</p>
              <h2 className="mb-0">{student.student_name || "Student"}</h2>
            </div>
            <div className="p-4">
              <div className="row g-3 align-items-start">
                <div className="col-md-auto">
                  <div style={{ width: "126px", height: "150px", border: "1px solid #e2e8f0", borderRadius: "10px", overflow: "hidden", background: "#f8fafc" }}>
                    {student.photo_url ? <img src={student.photo_url} alt={student.student_name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : null}
                  </div>
                </div>
                <div className="col">
                  <div className="row g-3">
                    <div className="col-md-4"><DetailTile label="Registration No" value={student.registration_no} /></div>
                    <div className="col-md-4"><DetailTile label="DOB" value={student.dob} /></div>
                    <div className="col-md-4"><DetailTile label="Father Name" value={student.father_name} /></div>
                    <div className="col-md-4"><DetailTile label="Course" value={student.course_name} /></div>
                    <div className="col-md-4"><DetailTile label="Session" value={student.session_year} /></div>
                    <div className="col-md-4"><DetailTile label="Study Centre" value={student.study_centre_name} /></div>
                    <div className="col-md-4"><DetailTile label="Centre Code" value={student.centre_code} /></div>
                    <div className="col-md-4"><DetailTile label="Admission Date" value={student.admission_date} /></div>
                    <div className="col-md-4"><DetailTile label="Completion Date" value={student.course_completion_date} /></div>
                    <div className="col-md-4"><DetailTile label="Verified At" value={result.verified_at} /></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default StudentVerification;
